import axios from "axios";
import router, { isPublicStandaloneRoute } from "../router/index";
import store from "../store";
import Vue from "vue";
import { BaseApiUrl } from "./base";
import applicationUserManager from "../Auth/applicationusermanager";
import { injectUnifiedPageSizeToRequest } from "../utils/elPaginationUnify";

export { axios };
export const base = BaseApiUrl;

// 请求延时
axios.defaults.timeout = 120000;

/** 批量导入/大批量写入等长耗时接口超时（毫秒）。最后修改：2026-08-31 */
export const LONG_REQUEST_TIMEOUT = 600000;

/** Token 绝对过期后，仍允许凭旧 Token 换新的宽限（分钟）。有操作时随请求滑动。最后修改：2026-09-22 */
const REFRESH_GRACE_MINUTES = 10;

/** 剩余有效期低于该阈值时主动换新（毫秒） */
const PROACTIVE_REFRESH_MS = 5 * 60 * 1000;

/** 单飞：并发 401 共用一次 RefreshToken，避免互相踢登录 */
let refreshPromise = null;

/** 抑制短时间重复「验证失效」Toast */
let lastAuthFailToastAt = 0;

var storeTemp = store;

/**
 * 解析本地过期时间（兼容 Date / ISO / toString）。最后修改：2026-09-22
 */
function parseExpireDate(value) {
  if (!value && value !== 0) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  const d = new Date(typeof value === "number" ? value : Date.parse(String(value)));
  return isNaN(d.getTime()) ? null : d;
}

function getStoredToken() {
  const token = storeTemp.state.token || window.localStorage.getItem("Token") || "";
  return token && token !== "undefined" ? token : "";
}

function getStoredTokenExpire() {
  return (
    storeTemp.state.tokenExpire ||
    window.localStorage.getItem("TokenExpire") ||
    ""
  );
}

/**
 * 写入新 Token 与过期时间，并同步可刷新窗口。最后修改：2026-09-22
 */
function applyNewToken(token, expiresInSeconds) {
  const seconds = Number(expiresInSeconds) || 0;
  const expiredate = new Date(Date.now() + seconds * 1000);
  const expireIso = expiredate.toISOString();
  store.commit("saveToken", token);
  store.commit("saveTokenExpire", expireIso);
  window.localStorage.expires_in = seconds;
  const refreshUntil = new Date(expiredate.getTime());
  refreshUntil.setMinutes(refreshUntil.getMinutes() + REFRESH_GRACE_MINUTES);
  window.localStorage.refreshtime = refreshUntil.toISOString();
}

function isLoginOrCallbackRoute() {
  const path = (router.currentRoute && router.currentRoute.path) || "";
  return path === "/login" || path === "/callback";
}

function isRefreshTokenRequest(config) {
  const url = (config && config.url) || "";
  return /\/login\/RefreshToken/i.test(url);
}

/**
 * 单飞刷新 Token。最后修改：2026-09-22
 */
function ensureTokenRefreshed() {
  if (refreshPromise) {
    return refreshPromise;
  }
  const token = getStoredToken();
  if (!token) {
    return Promise.resolve(null);
  }

  refreshPromise = refreshToken({ token })
    .then((res) => {
      if (res && res.success && res.response && res.response.token) {
        applyNewToken(res.response.token, res.response.expires_in);
        return res.response.token;
      }
      return null;
    })
    .catch(() => null)
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

axios.interceptors.request.use(
  (config) => {
    // 列表只传 page 时自动补每页条数（与统一分页联动）。最后修改：2026-08-24
    injectUnifiedPageSizeToRequest(config);

    // 有 Token 就带上，不要因本地 TokenExpire 提前剥离 Authorization（否则到期瞬间并发 401）。最后修改：2026-09-22
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = "Bearer " + token;
      // 记录发出时的 Token，用于识别「已换新后的旧 401」
      config.__requestToken = token;
    }

    saveRefreshtime();

    // 临近过期主动换新，减少操作中途突然 401。最后修改：2026-09-22
    if (token && !isRefreshTokenRequest(config)) {
      const expiretime = parseExpireDate(getStoredTokenExpire());
      if (expiretime) {
        const msLeft = expiretime.getTime() - Date.now();
        if (msLeft > 0 && msLeft < PROACTIVE_REFRESH_MS) {
          ensureTokenRefreshed();
        }
      }
    }

    return config;
  },
  (err) => {
    return Promise.reject(err);
  },
);

// http response 拦截
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    let errInfo = { success: false, message: "错误" };
    var originalRequest = error.config;
    if (
      error.code == "ECONNABORTED" &&
      error.message.indexOf("timeout") != -1 &&
      !originalRequest._retry
    ) {
      errInfo.message = "请求超时";
      originalRequest._retry = true;
    } else if (error.response) {
      if (error.response.status == 401) {
        return handleUnauthorized(error, originalRequest);
      } else if (error.response.status == 403) {
        errInfo.message = "失败！该操作无权";
      } else if (error.response.status == 429) {
        errInfo.message = "刷新次数过多，请稍事休息重试";
      } else if (error.response.status == 404) {
        errInfo.message = "失败！访问接口不存在";
      } else if (error.response.status == 500) {
        // 业务接口常以 HTTP 500 + MessageModel.msg 返回可读错误（如人脸检测失败）
        const data500 = error.response.data;
        const bizMsg500 =
          data500 && (data500.msg || data500.Msg || data500.message || data500.Message);
        errInfo.message = bizMsg500 || "失败！服务器异常";
      } else if (error.response.status == 405) {
        errInfo.message = "失败！请求http方法错误";
      } else if (error.response.status == 415) {
        errInfo.message = "失败！参数没有指定Body还是Query";
      } else {
        errInfo.message = "失败！请求错" + error.response.status;
      }
    } else {
      errInfo.message = "失败！服务器断开";
    }

    // blob 导出：业务失败可能是 JSON MessageModel，也可能是纯文本（如 BadRequest("没有数据可导出")）
    const isBlobRequest =
      originalRequest &&
      (originalRequest.responseType === "blob" ||
        originalRequest.responseType === "arraybuffer");
    const errData = error.response && error.response.data;
    if (isBlobRequest && typeof Blob !== "undefined" && errData instanceof Blob) {
      return errData
        .text()
        .then((text) => {
          const raw = String(text || "").trim();
          if (raw) {
            try {
              const j = JSON.parse(raw);
              if (typeof j === "string" && j.trim()) {
                errInfo.message = j.trim();
              } else {
                const bizMsg =
                  (j && (j.msg || j.message || j.Message || j.detail || j.title)) ||
                  "";
                errInfo.message = bizMsg || raw;
              }
            } catch (e) {
              errInfo.message = raw;
            }
          }
          return finalizeHttpError(error, originalRequest, errInfo);
        })
        .catch(() => finalizeHttpError(error, originalRequest, errInfo));
    }

    return finalizeHttpError(error, originalRequest, errInfo);
  },
);

/**
 * 处理 401：单飞刷新、旧请求让路、登录页不清新会话。最后修改：2026-09-22
 */
function handleUnauthorized(error, originalRequest) {
  const onPublicPage = isPublicStandaloneRoute(router.currentRoute);
  const errInfo = { success: false, message: "失败！验证失效，请重新登" };

  // RefreshToken 自身失败：不再递归刷新
  if (isRefreshTokenRequest(originalRequest)) {
    return finishAuthExpired(error, originalRequest, errInfo, onPublicPage);
  }

  const currentToken = getStoredToken();

  // 发出后 Token 已换新：直接用新 Token 重试，勿清登录态（修复登录成功同时弹验证失效）
  if (
    originalRequest &&
    originalRequest.__requestToken &&
    currentToken &&
    originalRequest.__requestToken !== currentToken
  ) {
    originalRequest.__isRetryRequest = true;
    originalRequest.headers = originalRequest.headers || {};
    originalRequest.headers.Authorization = "Bearer " + currentToken;
    return axios(originalRequest);
  }

  // 已重试过仍 401
  if (originalRequest && originalRequest.__isRetryRequest) {
    return finishAuthExpired(error, originalRequest, errInfo, onPublicPage);
  }

  const refreshtime = parseExpireDate(window.localStorage.refreshtime);
  const canRefresh =
    !!currentToken &&
    !!window.localStorage.refreshtime &&
    refreshtime &&
    new Date() <= refreshtime;

  if (canRefresh) {
    return ensureTokenRefreshed().then((newToken) => {
      if (newToken) {
        originalRequest.__isRetryRequest = true;
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = "Bearer " + newToken;
        return axios(originalRequest);
      }
      return finishAuthExpired(error, originalRequest, errInfo, onPublicPage);
    });
  }

  return finishAuthExpired(error, originalRequest, errInfo, onPublicPage);
}

/**
 * 认证最终失败：登录中的旧 401 不清理新 Token；Toast 去重。最后修改：2026-09-22
 */
function finishAuthExpired(error, originalRequest, errInfo, onPublicPage) {
  // 已在登录/回调页：多半是上一会话残留 401，清掉会打断正在进行的登录初始化
  if (isLoginOrCallbackRoute()) {
    if (onPublicPage) {
      return Promise.reject(error);
    }
    return errInfo;
  }

  ToLogin();

  if (onPublicPage) {
    return Promise.reject(error);
  }

  const now = Date.now();
  if (now - lastAuthFailToastAt < 3000) {
    return errInfo;
  }
  lastAuthFailToastAt = now;
  return finalizeHttpError(error, originalRequest, errInfo);
}

/**
 * 统一收尾：可选全局 Toast；blob 请求一律 reject，避免业务 .then 把错误 JSON 当文件下载。
 */
function finalizeHttpError(error, originalRequest, errInfo) {
  if (originalRequest && originalRequest.skipGlobalHttpErrorToast) {
    return Promise.reject(error);
  }
  Vue.prototype.$message({
    message: errInfo.message,
    type: "error",
  });
  const isBlobRequest =
    originalRequest &&
    (originalRequest.responseType === "blob" ||
      originalRequest.responseType === "arraybuffer");
  if (isBlobRequest) {
    // 把解析后的文案挂到 error 上，页面 catch 可复用，避免再弹一次时拿不到 msg
    error.bizMessage = errInfo.message;
    return Promise.reject(error);
  }
  return errInfo;
}

export const refreshToken = (params) => {
  return axios
    .get(`${base}/api/login/RefreshToken`, { params: params })
    .then((res) => res.data);
};

/**
 * 有操作时滑动「可刷新窗口」：取 Token 过期与当前时间较大者，再加宽限分钟。最后修改：2026-09-22
 */
export const saveRefreshtime = (params) => {
  const token = getStoredToken();
  if (!token) {
    return;
  }

  const nowtime = new Date();
  const expiretime = parseExpireDate(window.localStorage.TokenExpire) || nowtime;
  const lastRefreshtime = parseExpireDate(window.localStorage.refreshtime);

  // Token 与可刷新窗口均已过期：不再续期，下次 401 将踢回登录
  if (lastRefreshtime && lastRefreshtime < nowtime && expiretime < nowtime) {
    return;
  }

  const base = nowtime > expiretime ? nowtime : expiretime;
  const next = new Date(base.getTime());
  next.setMinutes(next.getMinutes() + REFRESH_GRACE_MINUTES);
  window.localStorage.refreshtime = next.toISOString();
};

/** 清除失效的登录态（大屏等公开页仅清缓存，不跳转登录） */
const clearAuthStorage = () => {
  store.commit("saveToken", "");
  store.commit("saveTokenExpire", "");
  store.commit("saveTagsData", "");
  window.localStorage.removeItem("user");
  window.localStorage.removeItem("NavigationBar");
  window.localStorage.removeItem("refreshtime");
};

const ToLogin = (params) => {
  // 登录页上的残留 401 不应清掉刚写入的新 Token。最后修改：2026-09-22
  if (isLoginOrCallbackRoute()) {
    return;
  }

  clearAuthStorage();

  if (isPublicStandaloneRoute(router.currentRoute)) {
    return;
  }

  if (global.IS_IDS4) {
    applicationUserManager.login();
  } else {
    router.replace({
      path: "/login",
      query: { redirect: router.currentRoute.fullPath },
    });
  }
};

export { clearAuthStorage, ToLogin };
